/**
 * `TripService` (o voucher cru + a projeção da linha do tempo) → a
 * `VoucherDocumentView` que o `VoucherDocument` do genui-kit desenha.
 *
 * A divisão é a mesma do `FlightDetailView`: **o host deriva, o kit desenha**. Tudo
 * o que exige ler o domínio — fuso do aeroporto, cidade por IATA, duração no
 * formato do fornecedor, dia do calendário local, cópia localizada — acontece aqui,
 * reusando os helpers de `derive.ts`/`labels.ts` que o painel já tem testados.
 *
 * A regra de caractere manda em cada linha: campo sem dado devolve `null` e a linha
 * (ou a seção inteira) não nasce. O documento nunca afirma o que a fonte não tem —
 * é por isso que empresa e a trilha de aprovação não aparecem: elas não existem no
 * `VoucherView`. Motivo e centro de custo só nascem quando o host passa o contexto
 * da solicitação.
 */
import { companionPeople, voucherTraveler } from './voucher_people.js';
import {
  airlineLocator,
  airportTimezone,
  cityByAirport,
  flightLegDirection,
  supplierConfirmation,
  travelerFacingLocator,
  type ApprovalRequestContext,
  type FlightHop,
  type FlightSegment,
} from '@voll-intelligence/domain';
import type {
  VoucherDocumentField,
  VoucherDocumentLeg,
  VoucherDocumentTicket,
  VoucherDocumentView,
} from './views/voucher_document_view.js';
import type { DocumentContext } from './context.js';
import { block, compact, compactBlocks, field } from './document_fields.js';
import { documentStatus } from './document_status.js';
import { hotelRules } from './hotel_cancellation.js';
import {
  calendarDateFromUtcDay,
  dayLabel,
  localHHMM,
  localWallClock,
  parseLocalIso,
  periodLabel,
  utcOffset,
} from './dates.js';
import { flightDurationLabel } from './flight_duration.js';
import { airlineIata, formatFlightDuration, money, placeLabel, splitAirport } from './format.js';
import { agencyLine, baggageFields, cabinLabel, MODAL_LABEL_KEY, stopsLabel } from './labels.js';
import type { ServiceAgency, TripService, Voucher } from './services.js';
import { requestInfo } from './request_info.js';

type BusSegment = Extract<NonNullable<Voucher['details']>, { kind: 'BUS' }>['segments'][number];
type HotelDetails = Extract<NonNullable<Voucher['details']>, { kind: 'HOTEL' }>;

// ── Aéreo ────────────────────────────────────────────────────────────────────

/** Assento · Localizador · Classe · Bagagem — o quarteto do BILHETE. Vive no
 *  segmento, não na perna: a fonte dá um assento e um localizador por bilhete, e
 *  repeti-los em cada voo afirmaria um assento por perna que não existe. */
function ticketFields(segment: FlightSegment, ctx: DocumentContext): VoucherDocumentField[] {
  // O código em destaque é o DA COMPANHIA (o que o balcão reconhece); quando a
  // reserva NDC/consolidador traz um interno diferente, ele desce para uma linha
  // secundária — o atendimento interno ainda o usa (issue #2090). Status do
  // fornecedor no lugar do código ("RESERVADO") não é código: a linha não nasce (#6368).
  const counterCode = travelerFacingLocator(segment);
  // O interno pela MESMA régua (aparado, e só código crível), senão um locator com
  // padding renderia o MESMO código duas vezes e um status viraria "código interno".
  const internalCode = travelerFacingLocator({ locator: segment.locator });
  const distinctInternal = internalCode !== counterCode ? internalCode : null;
  return compact([
    field('seat', ctx.t('trips.services.seat'), segment.seat),
    field('locator', ctx.t('trips.services.locator'), counterCode, {
      copy: ctx.t('trips.voucher.copyLocator'),
    }),
    field('internalLocator', ctx.t('trips.services.internalLocator'), distinctInternal),
    // A cabine reconhecida sai TRADUZIDA ("ECONOMY" → "Econômica"), como o
    // itinerário já faz; o que a lista fechada não reconhece (letra de reserva
    // "O", marca tarifária "LIGHT") passa cru — aqui o campo tem rótulo, então
    // o valor da fonte ainda informa.
    field(
      'class',
      ctx.t('trips.services.class'),
      cabinLabel(segment.cabinClass, ctx) ?? segment.cabinClass,
    ),
    // Duas linhas, e não uma: despacho e mão são franquias distintas, e a da
    // mão só nasce quando a tarifa a declara.
    ...baggageFields(segment, ctx).map((b) => field(b.key, b.label, b.value)),
  ]);
}

/** "EMIRATES 262" / "G3 1420" — o designador do voo. A fonte às vezes já manda o
 *  código da companhia DENTRO do número, e aí prefixar o nome dela escreve quem
 *  opera duas vezes ("GOL G3 1420"). A leitura é a mesma do `share_text`: número
 *  com letra já traz o prefixo. */
export function flightDesignator(carrier: string | null, number: string | null): string | null {
  const cia = carrier?.trim().toUpperCase() || null;
  const no = number?.trim();
  if (!no) return cia;
  return /[a-z]/i.test(no) ? no : [cia, no].filter(Boolean).join(' ');
}

/** "Direto · 14h35 · AZUL 4046" — quem voa, por quanto tempo, com quantas paradas.
 *  `cabin` é a exceção da perna: só chega quando a cabine desta perna DIFERE da
 *  do bilhete (ver `hopLeg`) — o caso das cabines mistas que o campo "Classe"
 *  sozinho esconderia. */
function legMeta(parts: {
  duration: string | null;
  stops: number | null;
  carrier: string | null;
  number: string | null;
  cabin?: string | null;
  ctx: DocumentContext;
}): string | null {
  const line = [
    stopsLabel(parts.stops, parts.ctx),
    parts.duration,
    flightDesignator(parts.carrier, parts.number),
    parts.cabin,
  ]
    .filter(Boolean)
    .join(' · ');
  // `stopsLabel` fala em minúscula porque nasceu no meio de uma frase da faixa do
  // painel ("1h15 · direto"); aqui ela ABRE a linha.
  return line ? line.replace(/^./, (c) => c.toUpperCase()) : null;
}

/** "chega ter, 25 ago" — só quando a chegada cai em OUTRA data. Nunca "+1": o
 *  sufixo obriga a pessoa a achar a data de partida e somar. */
function arrivalNote(
  depart: Date | null,
  arrive: Date | null,
  ctx: DocumentContext,
): string | null {
  if (!depart || !arrive || depart.toDateString() === arrive.toDateString()) return null;
  return ctx.t('trips.voucher.arrivesOn', { day: dayLabel(arrive, ctx.locale) });
}

/** "Conexão em Dubai · 3h25 de espera", com o aviso de troca de aeroporto quando a
 *  próxima decolagem é de outro — isso é corrida de táxi, não caminhada. */
function layoverNote(hop: FlightHop, ctx: DocumentContext): string | null {
  const layover = hop.layover;
  if (!layover) return null;
  const place =
    cityByAirport(layover.connectionAirport) ?? splitAirport(layover.connectionAirport).code;
  // `formatFlightDuration` lê dígitos puros como SEGUNDOS — é o formato que o
  // provedor manda e o que `waitSeconds` já é.
  const wait =
    layover.waitSeconds == null ? null : formatFlightDuration(String(layover.waitSeconds));
  const parts = [
    place ? ctx.t('trips.voucher.layoverAt', { place }) : ctx.t('trips.voucher.layover'),
    wait ? ctx.t('trips.voucher.layoverWait', { wait }) : null,
    layover.changeAirport ? ctx.t('trips.voucher.layoverChangeAirport') : null,
  ];
  return parts.filter(Boolean).join(' · ');
}

/** Um `hop` vira uma perna do bilhete — usado quando a fonte carregou o detalhe
 *  da conexão (o caminho "rico"; `segmentLeg` é o "pobre" sem hops). */
function hopLeg(
  hop: FlightHop,
  index: number,
  segment: FlightSegment,
  ctx: DocumentContext,
): VoucherDocumentLeg {
  const departAt = parseLocalIso(hop.departLocal) ?? hop.departAt;
  const arriveAt = parseLocalIso(hop.arriveLocal) ?? hop.arriveAt;
  // A cabine da perna só entra no meta quando DIFERE da do bilhete — o campo
  // "Classe" já afirma a do segmento, e repeti-la em cada perna leria como
  // mistura que não existe. A comparação é pelo RÓTULO resolvido, não pela
  // grafia crua: 'EXECUTIVA' e 'BUSINESS' são a mesma cabine em fontes
  // diferentes. Fora da lista fechada (`cabinLabel` → null) a perna cala: no
  // meta não há rótulo dando contexto, e a letra de reserva crua parece erro.
  const hopCabin = cabinLabel(hop.cabinClass, ctx);
  return {
    key: `hop-${String(index)}`,
    departTime: localHHMM(hop.departLocal, hop.departAt),
    departPlace: placeLabel(hop.departureAirport),
    arriveTime: localHHMM(hop.arriveLocal, hop.arriveAt),
    arrivePlace: placeLabel(hop.arrivalAirport),
    meta: legMeta({
      duration: flightDurationLabel(hop),
      stops: 0,
      carrier: hop.operatedBy ?? hop.airline,
      number: hop.flightNumber,
      cabin: hopCabin === cabinLabel(segment.cabinClass, ctx) ? null : hopCabin,
      ctx,
    }),
    arrivalNote: arrivalNote(departAt, arriveAt, ctx),
    connection: layoverNote(hop, ctx),
  };
}

/** O segmento como UMA perna — o caminho de quando a fonte não carregou os `hops`.
 *  Lista de hops vazia significa "o detalhe nunca veio", NÃO "o voo é direto": por
 *  isso a linha usa `stops`, que é quem sabe. */
function segmentLeg(segment: FlightSegment, ctx: DocumentContext): VoucherDocumentLeg {
  const departAt = parseLocalIso(segment.departLocal) ?? segment.departAt;
  const arriveAt = parseLocalIso(segment.arriveLocal) ?? segment.arriveAt;
  return {
    key: 'seg',
    departTime: localHHMM(segment.departLocal, segment.departAt),
    departPlace: placeLabel(segment.departureAirport),
    arriveTime: localHHMM(segment.arriveLocal, segment.arriveAt),
    arrivePlace: placeLabel(segment.arrivalAirport),
    meta: legMeta({
      duration: flightDurationLabel(segment),
      stops: segment.stops,
      carrier: segment.airline,
      number: segment.flightNumbers.join('/') || null,
      ctx,
    }),
    arrivalNote: arrivalNote(departAt, arriveAt, ctx),
  };
}

/** "IDA · SEG, 24 AGO" — o sentido resolvido pelo domínio (nunca por `type` cru) e
 *  o dia da partida. Sem sentido conhecido o bilhete se rotula pela posição. */
function ticketEyebrow(
  segment: FlightSegment,
  index: number,
  total: number,
  ctx: DocumentContext,
): string {
  const direction = flightLegDirection(segment);
  let label: string;
  if (direction === 'RETURN') {
    label = ctx.t('trips.services.legBack');
  } else if (direction === 'OUTWARD') {
    label = ctx.t('trips.services.legOut');
  } else if (total === 1) {
    label = ctx.t('trips.voucher.singleLeg');
  } else {
    label = ctx.t('trips.voucher.legN', { n: index + 1 });
  }
  const departAt = parseLocalIso(segment.departLocal) ?? segment.departAt;
  return departAt ? `${label} · ${dayLabel(departAt, ctx.locale)}` : label;
}

/** Um bilhete = um segmento — as pernas dentro dele vêm de `hopLeg`/`segmentLeg`,
 *  e o quarteto do bilhete (assento/localizador/classe/bagagem) só se pendura na
 *  ÚLTIMA perna, onde a fonte de fato o carrega. */
function flightTicket(
  segment: FlightSegment,
  index: number,
  segments: FlightSegment[],
  ctx: DocumentContext,
): VoucherDocumentTicket {
  const hops = segment.hops ?? [];
  const legs =
    hops.length > 0 ? hops.map((h, i) => hopLeg(h, i, segment, ctx)) : [segmentLeg(segment, ctx)];
  const last = legs.at(-1);
  if (last) last.fields = ticketFields(segment, ctx);
  // O aviso mora DEPOIS deste bilhete e fala do PRÓXIMO — é onde a régua troca de
  // localizador, e é ali que a pessoa precisa saber que a proteção acaba. A cópia
  // diz "o próximo trecho" justamente por isso; movê-la para o bilhete seguinte
  // colocaria o aviso depois da decisão que ele deveria informar.
  const next = segments[index + 1];
  // A régua da proteção é o código DA COMPANHIA: dois bilhetes com internos
  // diferentes mas o mesmo PNR são uma reserva só (issue #2090).
  const separate = next != null && airlineLocator(next) !== airlineLocator(segment);
  return {
    key: `t-${String(index)}`,
    eyebrow: ticketEyebrow(segment, index, segments.length, ctx),
    carrierIata: airlineIata(segment.airline),
    carrierName: segment.airline,
    legs,
    alert: separate
      ? {
          title: ctx.t('trips.voucher.separateTickets'),
          text: ctx.t('trips.voucher.separateTicketsNote'),
        }
      : null,
  };
}

/** A nota de fuso, só quando os aeroportos do voucher NÃO estão no mesmo fuso — com
 *  um fuso só ela não informa nada e vira ruído no pé de todo voucher doméstico. */
export function timezoneNote(segments: FlightSegment[], ctx: DocumentContext): string | null {
  const at = segments.map((s) => parseLocalIso(s.departLocal) ?? s.departAt).find((d) => d != null);
  // Os aeroportos das PERNAS entram junto: o de conexão tem horário na tela como
  // qualquer outro, e uma nota que explica o fuso de GRU e NRT mas cala o de DXB
  // deixa de fora justamente a hora que confunde.
  const airports = segments.flatMap((s) => [
    s.departureAirport,
    ...(s.hops ?? []).flatMap((h) => [h.departureAirport, h.arrivalAirport]),
    s.arrivalAirport,
  ]);
  const zones = new Map<string, string>();
  for (const airport of airports) {
    const zone = airportTimezone(airport);
    const code = splitAirport(airport).code;
    if (zone && code && !zones.has(code)) zones.set(code, zone);
  }
  if (new Set(zones.values()).size < 2) return null;
  const parts = [...zones].flatMap(([code, zone]) => {
    const offset = at ? utcOffset(zone, at) : null;
    return offset ? [`${code} ${offset}`] : [];
  });
  return parts.length > 0
    ? ctx.t('trips.voucher.localTimesWith', { zones: parts.join(' · ') })
    : ctx.t('trips.voucher.localTimes');
}

/** "Ida e volta · 24 Ago – 4 Set" — derivado dos bilhetes, nunca escolhido à mão. */
function flightSummary(segments: FlightSegment[], ctx: DocumentContext): string | null {
  const first = segments[0];
  if (!first) return null;
  // Contagem de segmentos sozinha mente: um multitrechos vendido em 2 bilhetes
  // (GRU→BSB, BSB→BEL, mesmo sentido) tem length 2 mas não é ida-e-volta — só é
  // quando o SEGUNDO segmento é a `flightLegDirection` RETURN, o mesmo sinal
  // que o cabeçalho já usa.
  const second = segments[1];
  const isRoundTrip = segments.length === 2 && !!second && flightLegDirection(second) === 'RETURN';
  let shape: string;
  if (segments.length === 1) {
    shape = ctx.t('trips.voucher.oneWay');
  } else if (isRoundTrip) {
    shape = ctx.t('trips.voucher.roundTrip');
  } else {
    shape = ctx.t('trips.voucher.multiLeg');
  }
  const from = parseLocalIso(first.departLocal) ?? first.departAt;
  const lastSeg = segments.at(-1);
  const to = lastSeg ? (parseLocalIso(lastSeg.arriveLocal) ?? lastSeg.arriveAt) : null;
  return from ? `${shape} · ${periodLabel(from, to, ctx.locale)}` : shape;
}

/** O último segmento da IDA — a corrida inicial de segmentos do mesmo sentido. É o
 *  que fecha a rota do cabeçalho: num multitrechos GRU→BSB→BEL o herói é GRU → BEL,
 *  e numa ida e volta ele para antes do trecho de volta. */
function lastOutboundSegment(segments: FlightSegment[]): FlightSegment | undefined {
  const first = segments[0];
  if (!first) return undefined;
  const direction = flightLegDirection(first);
  let last = first;
  for (const segment of segments.slice(1)) {
    if (flightLegDirection(segment) !== direction) break;
    last = segment;
  }
  return last;
}

/** A metade aérea da vista: rota da IDA no herói (não do último segmento —
 *  `lastOutboundSegment` já resolve isso), um bilhete por segmento. */
function flightParts(
  voucher: Voucher,
  segments: FlightSegment[],
  ctx: DocumentContext,
): Partial<VoucherDocumentView> {
  const first = segments[0];
  const from = first?.departureAirport;
  const to = lastOutboundSegment(segments)?.arrivalAirport;
  const fromCode = splitAirport(from).code;
  const toCode = splitAirport(to).code;
  const fromCity = cityByAirport(from);
  const toCity = cityByAirport(to);
  return {
    heading: fromCode && toCode ? `${fromCode} → ${toCode}` : fromCode || toCode || '—',
    subheading: fromCity && toCity ? `${fromCity} → ${toCity}` : null,
    summary: flightSummary(segments, ctx),
    companions: companionPeople(voucher),
    tickets: segments.map((segment, i) => flightTicket(segment, i, segments, ctx)),
    footnote: timezoneNote(segments, ctx),
  };
}

// ── Ônibus ───────────────────────────────────────────────────────────────────

function busTicket(
  segment: BusSegment,
  index: number,
  ctx: DocumentContext,
): VoucherDocumentTicket {
  let label: string;
  if (segment.type === 'BACK') {
    label = ctx.t('trips.services.legBack');
  } else if (segment.type === 'OUTWARD') {
    label = ctx.t('trips.services.legOut');
  } else {
    label = ctx.t('trips.voucher.legN', { n: index + 1 });
  }
  return {
    key: `b-${String(index)}`,
    eyebrow: segment.departAt ? `${label} · ${dayLabel(segment.departAt, ctx.locale)}` : label,
    carrierName: segment.company,
    legs: [
      {
        key: 'leg',
        departTime: localHHMM(null, segment.departAt),
        departPlace: segment.departureStation ?? '—',
        arriveTime: localHHMM(null, segment.arriveAt),
        arrivePlace: segment.arrivalStation ?? '—',
        arrivalNote: arrivalNote(segment.departAt, segment.arriveAt, ctx),
        fields: compact([
          field('seat', ctx.t('trips.services.seat'), segment.seat),
          field('locator', ctx.t('trips.services.locator'), segment.locator, {
            copy: ctx.t('trips.voucher.copyLocator'),
          }),
          field('class', ctx.t('trips.services.class'), segment.seatClass),
          field('from', ctx.t('trips.services.from'), segment.departureAddress, { full: true }),
          field('to', ctx.t('trips.services.to'), segment.arrivalAddress, { full: true }),
        ]),
      },
    ],
  };
}

// ── Hospedagem ───────────────────────────────────────────────────────────────

/** A metade de hospedagem: `checkIn`/`checkOut` (datas-só guardadas à meia-noite
 *  UTC) passam por `calendarDateFromUtcDay` antes do período — sem isso um fuso
 *  negativo mostra a estadia um dia adiantada. */
function hotelParts(
  voucher: Voucher,
  hotel: HotelDetails,
  ctx: DocumentContext,
): Partial<VoucherDocumentView> {
  const checkIn = calendarDateFromUtcDay(hotel.checkIn);
  const checkOut = calendarDateFromUtcDay(hotel.checkOut);
  const address = [hotel.address, hotel.city].filter(Boolean).join(' · ');
  const nights =
    hotel.nights == null ? null : ctx.t('trips.voucher.nights', { count: hotel.nights });
  const period = checkIn ? periodLabel(checkIn, checkOut, ctx.locale) : null;
  return {
    // O NOME do hotel primeiro — é o que a recepção reconhece e a mesma régua do
    // itinerário (`serviceHeading`). O `supplier` é fallback: em dados reais ele
    // às vezes carrega o canal de venda ("Acordo"), não o hotel. Truthiness, não
    // `??`: o schema permite `name: ''` no fio, e um `??` deixaria a string
    // vazia vencer o fornecedor — herói vazio e dialog sem nome acessível.
    heading: hotel.name?.trim() || voucher.supplier || '—',
    address: address ? { value: address, copy: ctx.t('trips.voucher.copyAddress') } : null,
    summary: [nights, period].filter(Boolean).join(' · ') || null,
    blocks: compactBlocks([
      block('stay', [
        checkIn
          ? {
              key: 'in',
              label: ctx.t('trips.voucher.checkIn'),
              value: dayLabel(checkIn, ctx.locale),
              hint: hotel.checkInHour
                ? ctx.t('trips.voucher.fromHour', { hour: hotel.checkInHour })
                : null,
            }
          : null,
        checkOut
          ? {
              key: 'out',
              label: ctx.t('trips.voucher.checkOut'),
              value: dayLabel(checkOut, ctx.locale),
              hint: hotel.checkOutHour
                ? ctx.t('trips.voucher.untilHour', { hour: hotel.checkOutHour })
                : null,
            }
          : null,
        // "Reserva", não "Localizador": hotel não tem localizador (a régua do
        // itinerário, `stopReference`). Código que não é do hotel — o nome do canal
        // ("VOLL"), o próprio voucher — não vira linha (issue #4256).
        field('reservation', ctx.t('trips.services.reservation'), supplierConfirmation(voucher), {
          copy: ctx.t('trips.voucher.copyReservation'),
          full: true,
        }),
      ]),
      block('room', [
        // "Acomodação", não "Quarto": o documento de voucher nomeia a categoria da
        // estadia (o protótipo "08 · voucher final" usa "Acomodação"); "Quarto"
        // (`trips.services.room`) segue no card de serviço/itinerário, outra tela.
        field('room', ctx.t('trips.voucher.accommodation'), hotel.roomDescription),
        field('guests', ctx.t('trips.services.guests'), guestLine(voucher, hotel)),
        // Um quarto é o caso normal e não é notícia — a linha só nasce quando a
        // reserva tem mais de um, que é quando o número muda o que se confere.
        field(
          'rooms',
          ctx.t('trips.services.rooms'),
          hotel.rooms != null && hotel.rooms > 1 ? String(hotel.rooms) : null,
        ),
      ]),
    ]),
    rules: hotelRules(voucher, hotel, ctx),
  };
}

/** "2 · Marcos Lima · Ana Prado" — quem se hospeda, e não só quantos: é o nome
 *  que a recepção pede, de TODOS os hóspedes; os acompanhantes da reserva fazem
 *  check-in pelo próprio nome. O protótipo escreve contagem e nomes no mesmo
 *  campo pela mesma razão. */
function guestLine(voucher: Voucher, hotel: HotelDetails): string | null {
  if (hotel.guests == null) return null;
  return (
    [String(hotel.guests), voucher.traveler?.name, ...voucher.companions.map((c) => c.name)]
      .filter(Boolean)
      .join(' · ') || null
  );
}

// ── Locação e mobilidade ─────────────────────────────────────────────────────

function journeyParts(service: TripService, ctx: DocumentContext): Partial<VoucherDocumentView> {
  const detail = service.detail;
  const voucher = service.voucher;
  if (detail?.kind === 'RENTAL') {
    return {
      heading: voucher.supplier ?? detail.carModel ?? '—',
      subheading: [detail.carModel, detail.carGroup].filter(Boolean).join(' · ') || null,
      // O DIA de retirada e devolução — os campos de agência só dizem a hora, e
      // um voucher de carro que não diz em que dia devolver não fecha a conta.
      summary: detail.pickupAt ? periodLabel(detail.pickupAt, detail.dropoffAt, ctx.locale) : null,
      blocks: compactBlocks([
        block('agency', [
          field(
            'pickup',
            ctx.t('trips.services.pickup'),
            agencyValue(detail.pickup, detail.pickupTime),
            {
              full: true,
            },
          ),
          field(
            'dropoff',
            ctx.t('trips.services.dropoff'),
            agencyValue(detail.dropoff, detail.dropoffTime),
            { full: true },
          ),
        ]),
      ]),
    };
  }
  if (detail?.kind === 'MOBILITY') {
    return {
      heading: detail.route ?? voucher.supplier ?? '—',
      blocks: compactBlocks([
        block('ride', [
          field('category', ctx.t('trips.voucher.category'), detail.category),
          field(
            'distance',
            ctx.t('trips.voucher.distance'),
            detail.distance == null
              ? null
              : ctx.t('trips.services.distanceValue', {
                  // O tradutor interpola com `String(value)` — sem isto, pt-BR
                  // mostra "12.4 km" (ponto) em vez de "12,4 km" (vírgula).
                  value: new Intl.NumberFormat(ctx.locale).format(detail.distance),
                }),
          ),
          field('duration', ctx.t('trips.voucher.duration'), detail.duration),
        ]),
      ]),
    };
  }
  return bareVoucherParts(service, ctx);
}

/** O voucher NÃO enriquecido (a fonte só completa `details` em CONFIRMED): o
 *  `journey` ainda sabe a rota e as datas, e é o que o documento pode afirmar —
 *  as mesmas leituras que a linha do tempo já faz (`genericAgendaRow`/
 *  `journeyWindow`). Rota só com as DUAS pontas; meia flecha diz menos que nada. */
function bareVoucherParts(
  service: TripService,
  ctx: DocumentContext,
): Partial<VoucherDocumentView> {
  const journey = service.voucher.journey;
  const route =
    journey?.origin && journey.destination ? `${journey.origin} → ${journey.destination}` : null;
  const startAt = localWallClock(journey?.startLocal, journey?.startAt ?? null);
  const endAt = localWallClock(journey?.endLocal, journey?.endAt ?? null);
  return {
    heading: route ?? service.voucher.supplier ?? ctx.t(MODAL_LABEL_KEY[service.modal]),
    summary: startAt ? periodLabel(startAt, endAt, ctx.locale) : null,
  };
}

/** "Localiza · Av. X, 100 · Recife · 09:00" — o balcão e a hora, uma linha só. */
function agencyValue(agency: ServiceAgency | null, time: string): string | null {
  return [agencyLine(agency), time].filter(Boolean).join(' · ') || null;
}

// ── O documento ──────────────────────────────────────────────────────────────

/** Despacha pelo `kind` do detalhe — cada modal tem sua própria anatomia de
 *  vista (aéreo tem bilhetes, hotel tem estadia, o resto cai no genérico). */
function modalParts(service: TripService, ctx: DocumentContext): Partial<VoucherDocumentView> {
  const details = service.voucher.details;
  if (details?.kind === 'FLIGHT') return flightParts(service.voucher, details.segments, ctx);
  if (details?.kind === 'HOTEL') return hotelParts(service.voucher, details, ctx);
  if (details?.kind === 'BUS') return busParts(service.voucher, details.segments, ctx);
  return journeyParts(service, ctx);
}

/** A metade de ônibus: herói = primeira estação de partida → última de chegada,
 *  mesmo com escalas — não o par do primeiro segmento sozinho. */
function busParts(
  voucher: Voucher,
  segments: BusSegment[],
  ctx: DocumentContext,
): Partial<VoucherDocumentView> {
  const first = segments[0];
  const last = segments.at(-1) ?? first;
  return {
    heading:
      first?.departureStation && last?.arrivalStation
        ? `${first.departureStation} → ${last.arrivalStation}`
        : (voucher.supplier ?? '—'),
    summary: first?.departAt
      ? periodLabel(first.departAt, last?.arriveAt ?? null, ctx.locale)
      : null,
    companions: companionPeople(voucher),
    tickets: segments.map((segment, i) => busTicket(segment, i, ctx)),
  };
}

/**
 * O documento completo de UM serviço da viagem.
 *
 * A ordem das seções é a do Figma "08 · voucher final": identidade → trechos (ou os
 * blocos do modal) → regras → valor com o selo de política → mais informações →
 * procedência.
 */
export function buildVoucherDocumentView(
  service: TripService,
  ctx: DocumentContext,
  request?: ApprovalRequestContext,
): VoucherDocumentView {
  const voucher = service.voucher;
  const dimmed = service.status === 'CANCELLED' || service.status === 'EXPIRED';
  const total = money(service.amount, service.currency);
  // O vocabulário do DOCUMENTO ("Aéreo", "Hospedagem"), não o dos tiles e do
  // calendário ("Voos", "Hotel"): o eyebrow nomeia a CATEGORIA do papel, e o
  // plural do menu de serviços lia como se o voucher tivesse vários.
  const modal = ctx.t(`trips.voucher.modal.${service.modal}`);
  return {
    eyebrow: service.voucherCode ? `${modal} · ${service.voucherCode}` : modal,
    status: documentStatus(service, ctx),
    heading: '—',
    ...voucherTraveler(voucher),
    ...modalParts(service, ctx),
    total: total
      ? {
          value: total,
          struck: dimmed,
          policy:
            voucher.within == null
              ? null
              : {
                  within: voucher.within,
                  label: ctx.t(
                    voucher.within ? 'trips.voucher.inPolicy' : 'trips.voucher.outPolicy',
                  ),
                },
        }
      : null,
    more: requestInfo(voucher, ctx, request),
    provenance: ctx.t('trips.voucher.issuedBy'),
  };
}

/** O resumo do voucher para a área de transferência — as MESMAS linhas de
 *  identidade que o topo do documento mostra, uma por linha. Deriva da vista já
 *  montada de propósito: um segundo formatador divergiria do documento no primeiro
 *  ajuste, e aí o que se cola deixa de ser o que se vê. */
export function voucherShareText(view: VoucherDocumentView): string {
  return [view.eyebrow, view.heading, view.subheading, view.summary, view.total?.value]
    .filter(Boolean)
    .join('\n');
}
